import annotationService from "api/annotationService";
import { IAnnotation } from "dto/IAnnotation";
import useGetPDFViewer from "hooks/useGetPDFViewer";
import { sortByCoordinatesOnPDF } from "utils/sortByCoordinatesOnPDF";
import styles from "./styles.module.scss";
import CloseIcon from "assets/icons/close.svg?react";
import CommentCard from "components/CommentCard";
import { FC, Props } from "./typings";
import { useEffect, useMemo, useRef, useState } from "react";
import RichEditor from "components/RichEditor";
import Button from "components/Button";
import { useTranslation } from "react-i18next";

const Comments: FC<Props> = ({
  toggle,
  fullscreen,
  onResize,
  showCopyToWord,
  activeAnnotation,
  setActiveAnnotation,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(activeAnnotation !== null);

  const [content, setContent] = useState("");
  const {
    PDF,
    getAnnotations,
    setAnnotations,
  } = useGetPDFViewer();

  const { mutate: deleteAnnotation } = annotationService.useDeleteAnnotation();
  const { mutate: updateComment } = annotationService.useUpdateComment();
  const { mutate: createComment } = annotationService.useCreateComment();
  const annotations = useMemo(() => {
    return getAnnotations() ?? [];
  }, [getAnnotations(), PDF]);

  useEffect(() => {
    if (!activeAnnotation) return;

    setEditMode(true);
    if (activeAnnotation?.Comments?.at(0)) {
      setContent(activeAnnotation?.Comments?.at(0).Text);
    } else {
      setContent("");
    }
  }, [activeAnnotation]);

  const handleDeleteAnnotation = (
    annotation: IAnnotation,
    onSuccess?: () => void
  ) => {
    deleteAnnotation(
      {
        AnnotationId: annotation.Id,
      },
      {
        onSuccess: () => {
          const annotations = [...getAnnotations()];
          setAnnotations(annotations.filter((a) => a.Id !== annotation.Id));
          onSuccess?.();
        },
      }
    );
  };

  const handleSave = () => {
    if (activeAnnotation) {
      if (!activeAnnotation?.Comments?.[0]) {
        createComment(
          {
            AnnotationId: activeAnnotation.Id,
            Text: content.replaceAll("<div>", "").replaceAll("</div>", ""),
          },
          {
            onSuccess: (response) => {
              const newComm = response.data;
              setEditMode(false);
              setActiveAnnotation(null);

              // Głębokie kopiowanie anotacji bez użycia JSON.parse
              const annotations = [...getAnnotations()].map((annotation) => ({
                ...annotation,
                Comments: [...annotation.Comments],
              }));

              const el = annotations.find((a) => a.Id === activeAnnotation.Id);
              if (!el) return;

              el.Comments = [newComm];

              setAnnotations(annotations);
              setContent("");
            },
          }
        );
        return;
      }

      updateComment(
        {
          CommentId: activeAnnotation.Comments.at(0).Id,
          Text: content.replaceAll("<div>", "").replaceAll("</div>", ""),
        },
        {
          onSuccess: (response) => {
            const newComm = response.data;
            setEditMode(false);
            setActiveAnnotation(null);

            // Głębokie kopiowanie anotacji
            const annotations = [...getAnnotations()].map((annotation) => ({
              ...annotation,
              Comments: [...annotation.Comments],
            }));

            const el = annotations.find((a) => a.Id === activeAnnotation.Id);
            if (!el) return;

            const comment = el.Comments.find((c) => c.Id === newComm.Id);
            comment.Text = response.data.Text;

            setAnnotations(annotations);
            setContent("");
          },
        }
      );
      return;
    }
  };

  const handleEditMode = (annotation?: IAnnotation) => {
    console.log("Handle edit mode", annotation);
    setEditMode(true);
    if (!annotation) {
      setActiveAnnotation(null);
      setContent("");
      return;
    }
    setActiveAnnotation(annotation);
    setContent(annotation?.Comments?.[0]?.Text ?? "");
  };

  const getHeader = () => {
    if (editMode) {
      if (activeAnnotation && !activeAnnotation?.Comments?.[0]) {
        return t("pdf.newAnnotation");
      }

      return t("pdf.editAnnotation");
    }

    return t("pdf.annotationsList");
  };

  const [resizeWindow] = useState(false);
  const [, setWidth] = useState(450);

  const ref = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: any) => {
    if (!ref?.current) return;
    const position = ref.current.getBoundingClientRect();
    if (resizeWindow) {
      let newWidth = 0;
      if (fullscreen) {
        newWidth = e.clientX - position.x + 2;
      } else {
        newWidth = e.clientX + 4;
      }
      setWidth(newWidth);
      onResize?.(newWidth);
      localStorage.setItem("comments_width", newWidth.toString());
    }
  };

  useEffect(() => {
    const item = localStorage.getItem("comments_width");
    if (!item) return;
    setWidth(Number(item));
    onResize?.(Number(item));
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [resizeWindow]);

  const handleClose = () => {
    if (activeAnnotation && !activeAnnotation?.Comments?.[0]) {
      handleDeleteAnnotation(activeAnnotation, () => {
        toggle();
      });
    } else {
      toggle();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <div>{getHeader()}</div>
        <CloseIcon onClick={handleClose} className={styles.closeIcon} />
      </div>
      <div className={styles.divider}></div>
      <div className={styles.list}>
        {editMode && (
          <div className={styles.editorWrapper}>
            <div className={styles.editor}>
              <RichEditor content={content} setContent={(t) => setContent(t)} />
            </div>
            <div className={styles.buttonsWrapper}>
              <div className={styles.divider}></div>
              <div className={styles.buttons}>
                <Button text={t("common.save")} onClick={handleSave} />
              </div>
            </div>
          </div>
        )}
        {!editMode && (
          <div className={styles.listWrapper}>
            {annotations
              ?.sort(sortByCoordinatesOnPDF)
              ?.map((annotation: IAnnotation) => (
                <CommentCard
                  handleDeleteAnnotation={(a) =>
                    handleDeleteAnnotation(a, null)
                  }
                  handleEditMode={handleEditMode}
                  annotation={annotation}
                  key={annotation.Id}
                  showCopyToWord={showCopyToWord}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Comments;
